<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Sistem"
      title="Cron Job İzleyici"
      description="Arka plan job sağlık durumlarını, çalışma frekanslarını ve son execution sonuçlarını operasyonel bir panel üzerinden izleyin."
    >
      <template #actions>
        <div class="admin-page-toolbar__actions">
          <v-btn-toggle
            v-model="viewMode"
            mandatory
            dense
          >
            <v-btn small>
              Kartlar
            </v-btn>
            <v-btn small>
              Tablo
            </v-btn>
          </v-btn-toggle>

          <v-btn
            outlined
            color="primary"
            small
            :loading="loading"
            @click="fetchCronJobs"
          >
            Yenile
          </v-btn>
        </div>
      </template>
    </admin-page-header>

    <v-alert
      v-if="errorMessage"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>

    <admin-metric-strip
      :items="summaryCards"
      :loading="loading"
    />
    <div
      class="d-flex align-center flex-wrap"
      style="gap: 16px;"
    >
      <v-checkbox
        v-model="attentionOnly"
        label="Yalnız hata / uyarı"
        hide-details
        class="mt-0"
      />
      <span class="text-caption">Sunucu işleri · Ortalama başarı, kayıtlı çalışması olan işlerden hesaplanır; kaydı olmayan işler dahil edilmez.</span>
    </div>

    <template v-if="viewMode === 0">
      <v-row>
        <v-col
          v-for="job in orderedJobs"
          :key="job.id"
          cols="12"
          md="6"
          xl="4"
        >
          <v-card
            class="admin-dashboard-card cron-job-card"
            outlined
          >
            <v-card-text>
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="font-weight-bold">
                    {{ job.name }}
                  </div>
                  <div class="text-caption text--secondary mt-1">
                    {{ job.scheduleHuman }}
                  </div>
                </div>
                <v-chip
                  small
                  outlined
                  :color="statusColor(job.status)"
                >
                  {{ statusLabel(job.status) }}
                </v-chip>
              </div>

              <p class="text-caption mt-4 mb-4 cron-job-card__description">
                {{ job.description }}
              </p>

              <div class="cron-job-card__metrics">
                <div class="cron-job-card__metric">
                  <span>Son süre</span>
                  <strong>{{ formatDuration(job.totalRuns > 0 ? job.lastDuration : null) }}</strong>
                </div>
                <div class="cron-job-card__metric">
                  <span>Ortalama</span>
                  <strong>{{ formatDuration(job.totalRuns > 0 ? job.avgDuration : null) }}</strong>
                </div>
                <div class="cron-job-card__metric">
                  <span>Başarı</span>
                  <strong>{{ formatSuccessRate(job) }}</strong>
                </div>
              </div>

              <v-sparkline
                :value="job.history.map(entry => entry.duration)"
                :color="statusColor(job.status)"
                line-width="2"
                padding="8"
                smooth
                auto-draw
                class="mt-4"
              />

              <div class="d-flex align-center justify-space-between mt-4">
                <div class="text-caption text--secondary">
                  Sonraki çalışma: {{ job.nextRun }}
                </div>
                <v-btn
                  text
                  small
                  color="primary"
                  @click="selectedJob = job"
                >
                  Detay
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <v-card
      v-else
      class="admin-table-card"
      outlined
    >
      <v-data-table
        :headers="cronHeaders"
        :items="orderedJobs"
        :footer-props="tableFooterProps"
        class="admin-data-table elevation-0"
      >
        <template v-slot:item.status="{ item }">
          <v-chip
            small
            outlined
            :color="statusColor(item.status)"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <template v-slot:item.lastDuration="{ item }">
          {{ formatDuration(item.totalRuns > 0 ? item.lastDuration : null) }}
        </template>

        <template v-slot:item.avgDuration="{ item }">
          {{ formatDuration(item.totalRuns > 0 ? item.avgDuration : null) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            small
            color="primary"
            @click="selectedJob = item"
          >
            <v-icon>{{ icons.mdiEyeOutline }}</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="detailDialog"
      max-width="720px"
    >
      <v-card class="admin-dialog-card">
        <v-toolbar
          dense
          :elevation="0"
        >
          <v-toolbar-title>{{ selectedJob ? selectedJob.name : 'Cron Detayı' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="selectedJob = null"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text
          v-if="selectedJob"
          class="pa-6"
        >
          <p class="text-body-2 mb-4">
            {{ selectedJob.description }}
          </p>

          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <div class="cron-detail__meta">
                <span>Sıklık</span>
                <strong>{{ selectedJob.schedule }} / {{ selectedJob.scheduleHuman }}</strong>
              </div>
              <div class="cron-detail__meta">
                <span>Son çalışma</span>
                <strong>{{ selectedJob.lastRun }}</strong>
              </div>
              <div class="cron-detail__meta">
                <span>Sonraki çalışma</span>
                <strong>{{ selectedJob.nextRun }}</strong>
              </div>
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <div class="cron-detail__meta">
                <span>Toplam run</span>
                <strong>{{ selectedJob.totalRuns }}</strong>
              </div>
              <div class="cron-detail__meta">
                <span>Başarısız run</span>
                <strong>{{ selectedJob.failedRuns }}</strong>
              </div>
              <div class="cron-detail__meta">
                <span>Başarı oranı</span>
                <strong>{{ formatSuccessRate(selectedJob) }}</strong>
              </div>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <div class="font-weight-bold mb-3">
            Son 7 çalışma
          </div>

          <p
            v-if="!selectedJob.history.length"
            class="text-body-2 text--secondary"
          >
            Henüz tamamlanmış çalışma kaydı yok. Bu iş başarı ortalamasına dahil edilmez.
          </p>

          <v-alert
            v-if="selectedJob.lastMessage"
            dense
            text
            color="info"
            class="mb-4"
          >
            {{ selectedJob.lastMessage }}
          </v-alert>

          <div
            v-for="entry in selectedJob.history"
            :key="`${selectedJob.id}-${entry.time}`"
            class="cron-history-row"
          >
            <div>
              <div class="font-weight-medium">
                {{ entry.time }}
              </div>
              <div class="text-caption text--secondary">
                {{ statusLabel(entry.status) }}
              </div>
            </div>
            <span
              class="cron-history-row__pill"
              :style="{ color: statusColor(entry.status), backgroundColor: statusSoftColor(entry.status) }"
            >
              {{ formatDuration(entry.duration) }}
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { mdiClose, mdiEyeOutline } from '@mdi/js'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import AdminMetricStrip from '@/components/admin/AdminMetricStrip.vue'
import { getAccessToken } from '@/utils/panelSession'

export default {
  name: 'CronMonitorPage',
  components: {
    AdminPageHeader,
    AdminMetricStrip,
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      viewMode: 1,
      attentionOnly: false,
      selectedJob: null,
      icons: {
        mdiClose,
        mdiEyeOutline,
      },
      cronHeaders: [
        { text: 'Job Adı', value: 'name', sortable: false },
        { text: 'Sıklık', value: 'scheduleHuman', sortable: false },
        { text: 'Son Çalışma', value: 'lastRun', sortable: false },
        { text: 'Son Süre', value: 'lastDuration', sortable: false },
        { text: 'Ortalama', value: 'avgDuration', sortable: false },
        { text: 'Durum', value: 'status', sortable: false },
        { text: 'Sonraki Çalışma', value: 'nextRun', sortable: false },
        {
          text: 'İşlemler', value: 'actions', sortable: false, align: 'center',
        },
      ],
      cronJobs: [],
    }
  },
  computed: {
    orderedJobs() {
      const rank = {
        failed: 0, warning: 1, running: 2, idle: 3, success: 4,
      }

      return this.cronJobs.filter(job => !this.attentionOnly || ['failed', 'warning'].includes(job.status))
        .slice().sort((left, right) => (rank[left.status] ?? 5) - (rank[right.status] ?? 5))
    },
    summaryCards() {
      const total = this.cronJobs.length
      const running = this.cronJobs.filter(job => job.status === 'running').length
      const healthy = this.cronJobs.filter(job => job.status === 'success').length
      const warning = this.cronJobs.filter(job => job.status === 'warning').length
      const failed = this.cronJobs.filter(job => job.status === 'failed').length
      const recordedJobs = this.cronJobs.filter(job => Number(job.totalRuns) > 0)
      const average = recordedJobs.length
        ? `%${(recordedJobs.reduce((sum, job) => sum + Number(job.successRate), 0) / recordedJobs.length).toFixed(1)}`
        : '—'

      return [
        { label: 'Toplam Job', value: total, color: '#0d6efd' },
        { label: 'Çalışıyor', value: running, color: '#16B1FF' },
        { label: 'Son çalışma başarılı', value: healthy, color: '#287241' },
        { label: 'Uyarı', value: warning, color: '#FFB400' },
        { label: 'Hata', value: failed, color: '#FF4C51' },
        { label: 'Ort. Başarı', value: average, color: '#d41028' },
      ]
    },
    tableFooterProps() {
      return {
        'items-per-page-options': [10, 20, 50, 100],
      }
    },
    detailDialog() {
      return !!this.selectedJob
    },
  },
  mounted() {
    this.attentionOnly = this.$route.query.attention === '1'
    this.fetchCronJobs()
  },
  methods: {
    async fetchCronJobs() {
      this.loading = true
      this.errorMessage = ''
      try {
        const token = getAccessToken()
        const response = await axios.get(`${process.env.VUE_APP_BASE_API_URL}admin/system/cron/jobs`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        const jobs = response?.data?.data?.jobs

        if (!Array.isArray(jobs)) {
          throw new Error('Cron job servisi geçersiz cevap döndürdü.')
        }

        this.cronJobs = jobs
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || error?.message || 'Cron job verisi alınamadı.'
      } finally {
        this.loading = false
      }
    },
    statusColor(status) {
      return ({
        running: '#0d6efd',
        success: '#56CA00',
        failed: '#FF4C51',
        warning: '#FFB400',
        idle: '#8A8D93',
      })[status] || '#8A8D93'
    },
    statusSoftColor(status) {
      return ({
        running: 'rgba(13, 110, 253, 0.12)',
        success: 'rgba(86, 202, 0, 0.12)',
        failed: 'rgba(255, 76, 81, 0.12)',
        warning: 'rgba(255, 180, 0, 0.12)',
        idle: 'rgba(138, 141, 147, 0.12)',
      })[status] || 'rgba(138, 141, 147, 0.12)'
    },
    statusLabel(status) {
      return ({
        running: 'Çalışıyor',
        success: 'Başarılı',
        failed: 'Başarısız',
        warning: 'Uyarı',
        idle: 'Kayıt yok',
      })[status] || status
    },
    formatSuccessRate(job) {
      return Number(job.totalRuns) > 0 ? `%${job.successRate}` : '—'
    },
    formatDuration(ms) {
      if (ms === null || ms === undefined) return '—'
      if (ms < 1000) return `${ms}ms`
      if (ms < 60000) return `${(ms / 1000).toFixed(1)}sn`

      return `${(ms / 60000).toFixed(1)}dk`
    },
  },
}
</script>

<style lang="scss" scoped>
.cron-summary-card {
  min-height: 124px;
}

.cron-job-card {
  min-height: 320px;
}

.cron-job-card__description {
  line-height: 1.7;
  min-height: 68px;
}

.cron-job-card__metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.cron-job-card__metric {
  padding: 12px;
  border-radius: 14px;
  background: rgba(148, 163, 184, 0.08);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cron-job-card__metric span {
  font-size: 11px;
  color: rgba(148, 163, 184, 0.96);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.cron-job-card__metric strong {
  font-size: 14px;
}

.cron-detail__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 0;
}

.cron-detail__meta + .cron-detail__meta {
  border-top: 1px solid rgba(148, 163, 184, 0.12);
}

.cron-detail__meta span {
  font-size: 11px;
  color: rgba(148, 163, 184, 0.96);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.cron-history-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
}

.cron-history-row__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}
</style>
